import React, { useEffect } from "react";
import PropTypes from "prop-types";
// hooks
import { useParams, useHistory, useLocation } from "react-router-dom";
import useOrderCreateFlow from "../../../hooks/reduxContainer/fulfillment/orderCreateFlow";
import { useOrderCreate, useOrderUpdate } from "../../../hooks/api/fulfillment/orders";
import { useCreateShipment } from "../../../hooks/api/fulfillment/shipment";
import { useFeaturePreviewStore } from "../../../components/FeaturePreviewModal";

// components
import FulfillmentBackButton from "../FulfillmentBackButton";
import { CheckOutlined, LoadingOutlined } from "@ant-design/icons";
import { Collapse, notification, Modal } from "antd";
import SelectLocationStep from "./SelectLocationStep";
import SelectAddressStep from "./SelectAddressStep";
import SearchCustomerStep from "./SearchCustomerStep";
import SelectProductsStep from "./SelectProductsStep";
import ProvideExternalInfoStep from "./ProvideExternalInfoStep";
import OrderOverviewStep from "./OrderOverviewStep";

// lib, helpers
import Order from "../../../lib/fulfillment/order";
import Organization from "../../../lib/organization";
import { isEmpty, get } from "lodash-es";
import queryString from "query-string";
import { objectToFormData } from "object-to-formdata";
import { Organization as OrganizationLib } from "@secondcloset/fulfillment-utils";

const OrderCreatePage = () => {
  const {
    steps,
    activeStepKey,
    activeLocation,
    externalPlatformVersion,
    activeCustomer,
    activeAddress,
    itemList,
    notes,
    externalOrderNumber,
    isAutoCreateDeliveryShipment,
    insuranceValue,
    files,
    dispatchSetActiveStepKey,
    dispatchResetOrderCreateFlow,
    dispatchSetAutoCreateDeliveryShipment,
  } = useOrderCreateFlow();
  const { orderCreateDetails, createOrder } = useOrderCreate();
  const { orderUpdateDetails, updateLoading, updateOrder } = useOrderUpdate();
  const { shipment, createShipment } = useCreateShipment();
  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const organizationID = get(params, "organization_id");

  const { feature } = useFeaturePreviewStore();

  const createDeliveryUntrackedShipment = async (order) => {
    const orderID = get(order, "id");
    const untrackedShipmentBody = {
      consume_stock: false,
      job_type: "delivery",
      order_id: orderID,
      ...(feature.shipmentCreate
        ? {
            version: 2,
            items: itemList.map((item) => ({
              product_id: item.id,
              quantity: item.quantity,
            })),
          }
        : { version: 1, order_items: get(order, "items", []).map((item) => ({ id: item.id })) }),
      untracked: true,
    };

    return createShipment(untrackedShipmentBody);
  };

  // eslint-disable-next-line
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const parsed = queryString.parse(location.search);
    const isAutoCreateShipment = get(parsed, "isAutoCreateShipment", false);
    if (!isAutoCreateDeliveryShipment && isAutoCreateShipment) {
      dispatchSetAutoCreateDeliveryShipment(true);
    }
    dispatchSetActiveStepKey(steps.selectLocation.key);
    return () => dispatchResetOrderCreateFlow();
  }, []);

  useEffect(() => {
    const order = orderCreateDetails.data;
    if (order) {
      if (isAutoCreateDeliveryShipment) {
        createDeliveryUntrackedShipment(order);
      } else {
        if (!orderUpdateDetails.loading) history.push(`/fulfillment/orders/${order.id}`);
      }
    }
  }, [orderCreateDetails.data, orderUpdateDetails.loading]);

  useEffect(() => {
    if (shipment.data) {
      const orderID = get(shipment.data, "external_order_id");
      history.push(`/fulfillment/orders/${orderID}/create-shipment?isReturn=true`);
    }
  }, [shipment.data]);

  useEffect(() => {
    const error = orderCreateDetails.error || shipment.error;
    if (error) {
      notification.error({
        message: "Create Order Error",
        description: error,
      });
    }
  }, [orderCreateDetails.error, shipment.error]);

  const renderHeader = () => {
    return (
      <div className="header">
        <FulfillmentBackButton onClick={showDiscardModal} label="Back To Organization" />
      </div>
    );
  };

  const renderStepHeader = (step) => {
    const { title, seq, key } = step;
    const completed = isStepCompleted(key);
    const icon = completed ? (
      <div className="complete-icon">
        <CheckOutlined />
      </div>
    ) : (
      <div className="step-number">Step {seq}</div>
    );

    return (
      <div className="step-header">
        {icon}
        <div className="step-title">{title}</div>
      </div>
    );
  };

  const renderStepContent = (stepKey) => {
    switch (stepKey) {
      case steps.selectLocation.key:
        return <SelectLocationStep />;
      case steps.searchCustomer.key:
        return <SearchCustomerStep />;
      case steps.selectAddress.key:
        return <SelectAddressStep />;
      case steps.selectProducts.key:
        return <SelectProductsStep />;
      case steps.provideExternalInfo.key:
        return <ProvideExternalInfoStep />;
      case steps.orderOverview.key:
        return <OrderOverviewStep />;
      default:
        return null;
    }
  };

  const isAllStepCompleted = () => {
    return Object.keys(steps).every((key) => (key === steps.orderOverview.key ? true : isStepCompleted(key)));
  };

  const isStepCompleted = (stepKey) => {
    switch (stepKey) {
      case steps.selectLocation.key:
        return !!activeLocation;
      case steps.searchCustomer.key:
        return !!activeCustomer;
      case steps.selectAddress.key:
        return !!activeAddress;
      case steps.selectProducts.key:
        return !isEmpty(itemList);
      case steps.provideExternalInfo.key:
        return !!externalOrderNumber;
      default:
        return false;
    }
  };

  const isStepDisable = (stepKey) => {
    const currentSeq = steps[stepKey].seq;
    if (currentSeq === 1) return false;
    const prevSeq = currentSeq - 1;
    const prevStep = Object.values(steps).find((step) => step.seq === prevSeq);
    return !isStepCompleted(prevStep.key);
  };

  const goToNextStep = (stepKey) => {
    if (stepKey === "selectProducts") {
      // TODO: remove this when we have a better way to handling large orders
      const maxQuantity = Organization.getMaxFulfillableQuantity(organizationID);
      const itemCount = itemList?.reduce((acc, cv) => cv?.quantity + acc, 0) || 0;
      if (itemCount > maxQuantity) {
        notification.warning({
          message: `Total item quantity must less than ${maxQuantity}`,
        });
        return;
      }
    }

    const step = steps[stepKey];
    const sequence = step.seq;
    const nextSeq = sequence + 1;
    const nextStep = Object.values(steps).find((step) => step.seq === nextSeq);
    if (nextStep) {
      dispatchSetActiveStepKey(nextStep.key);
    }
  };

  const onCreateOrderClicked = async () => {
    const isIKEA = Organization.isIkeaOrgID(organizationID);
    const isStaging = process.env.REACT_APP_ENV === "staging";

    // BE rely on (platform === centiro) to tell if an order is ikea
    const platform = isIKEA && isStaging ? "centiro" : "manual";

    const body = Order.getCreateOrderBody({
      itemList,
      customerInformation: activeCustomer,
      addressInformation: activeAddress,
      external_order_number: externalOrderNumber,
      external_platform_version: externalPlatformVersion,
      internal_notes: notes,
      fulfilled_from: activeLocation || OrganizationLib.DEFAULT_FALLBACK_FACILITY_CODE,
      platform,
      insurance_value: insuranceValue,
    });

    body.orders.organization_id = organizationID;
    if (files.length) updateLoading(true);
    const order = await createOrder(body);
    if (files.length && order) {
      const fileList = files.map((file) => ({ file: file?.originFileObj }));
      const formData = objectToFormData({
        orders: {
          files_attributes: fileList,
        },
      });
      await updateOrder(order.id, formData);
    }
  };

  const renderNextButton = (stepKey) => {
    const disabled = !isStepCompleted(stepKey);
    const isCurrentStep = activeStepKey === stepKey;
    const className = `step-next-button ${disabled && "disabled"} ${isCurrentStep && "currentStep"}`;
    const onClick = disabled ? () => {} : () => goToNextStep(stepKey);
    return (
      <div className="step-next-button-wrap">
        <div className={className} onClick={onClick}>
          NEXT
        </div>
      </div>
    );
  };

  const renderCreateOrderButton = () => {
    if (orderCreateDetails.loading || orderUpdateDetails.loading) {
      return (
        <div className="loading-button">
          <LoadingOutlined />
        </div>
      );
    }
    const disabled = !isAllStepCompleted();
    const className = `create-order-button ${disabled && "disabled"}`;
    const onClick = disabled ? () => {} : onCreateOrderClicked;
    return (
      <div className="step-next-button-wrap">
        <div className={className} onClick={onClick}>
          Create Order
        </div>
      </div>
    );
  };

  const renderAllSteps = () => {
    return Object.values(steps)
      .sort((s1, s2) => s1.seq - s2.seq)
      .map((step, index) => {
        const { key } = step;
        const stepHeader = renderStepHeader(step);
        const isActive = step.key === activeStepKey;
        const isFinalStep = step.key === steps.orderOverview.key;
        return (
          <Collapse.Panel
            header={stepHeader}
            showArrow={false}
            className={`collapse-panel-wrap ${isActive && "active"}`}
            disabled={isStepDisable(step.key)}
            key={key}
          >
            <div className={`create-order-step-${index + 1} ${isActive ? "active" : ""}`}>{renderStepContent(key)}</div>
            {isFinalStep ? renderCreateOrderButton() : renderNextButton(key)}
          </Collapse.Panel>
        );
      });
  };

  const showDiscardModal = () => {
    Modal.confirm({
      title: "Are you sure you want to discard the change?",
      okText: "Yes",
      cancelText: "No",
      onOk: () => {
        history.push(`/organizations/${organizationID}`);
      },
    });
  };

  return (
    <div className="page-container" id="fulfillment-order-create-page">
      {renderHeader()}
      <Collapse
        className="collapse-container"
        activeKey={[activeStepKey]}
        onChange={(activeKeys) => {
          const lastKey = activeKeys[activeKeys.length - 1] || null;
          dispatchSetActiveStepKey(lastKey);
        }}
      >
        {renderAllSteps()}
      </Collapse>
    </div>
  );
};

OrderCreatePage.propTypes = {
  isReturn: PropTypes.bool,
};

export default OrderCreatePage;
